import type {
  OrchestrationShellSnapshot,
  OrchestrationShellStreamItem,
} from "@afar-research/contracts";
import * as Deferred from "effect/Deferred";
import * as Effect from "effect/Effect";
import * as Fiber from "effect/Fiber";
import * as Option from "effect/Option";
import * as Ref from "effect/Ref";
import type * as Scope from "effect/Scope";
import * as Stream from "effect/Stream";
import * as SubscriptionRef from "effect/SubscriptionRef";

import { applyShellStreamEvent } from "../state/shellReducer.ts";
import {
  ClientTransportResources,
  forkClientTransportSubscription,
} from "../connection/resources.ts";

export interface SynchronizedHostViewState {
  readonly generation: number;
  readonly phase: "synchronizing" | "ready";
  readonly snapshot: Option.Option<OrchestrationShellSnapshot>;
}

export interface HostViewSynchronization {
  readonly state: SubscriptionRef.SubscriptionRef<SynchronizedHostViewState>;
  readonly synchronize: <E>(
    stream: Stream.Stream<OrchestrationShellStreamItem, E>,
  ) => Effect.Effect<
    {
      readonly ready: Effect.Effect<void, E>;
      readonly completed: Effect.Effect<void, E>;
    },
    never,
    Scope.Scope
  >;
  readonly invalidate: Effect.Effect<void>;
}

const initialState: SynchronizedHostViewState = {
  generation: 0,
  phase: "synchronizing",
  snapshot: Option.none(),
};

export const makeHostViewSynchronization = Effect.fn("HostViewSynchronization.make")(function* (
  ownerIdentity = "unassigned",
) {
  const resources = yield* ClientTransportResources;
  const activeGeneration = yield* Ref.make(0);
  const state = yield* SubscriptionRef.make<SynchronizedHostViewState>(initialState);
  const activeCleanup = yield* Ref.make<Effect.Effect<void>>(Effect.void);
  const cancelActive = Effect.gen(function* () {
    const cleanup = yield* Ref.getAndSet(activeCleanup, Effect.void);
    yield* cleanup;
  });

  const invalidate = Ref.updateAndGet(activeGeneration, (generation) => generation + 1).pipe(
    Effect.flatMap((generation) =>
      SubscriptionRef.set(state, {
        generation,
        phase: "synchronizing",
        snapshot: Option.none(),
      }),
    ),
    Effect.andThen(cancelActive),
    Effect.asVoid,
  );

  const synchronize = <E>(
    stream: Stream.Stream<OrchestrationShellStreamItem, E>,
  ): Effect.Effect<
    {
      readonly ready: Effect.Effect<void, E>;
      readonly completed: Effect.Effect<void, E>;
    },
    never,
    Scope.Scope
  > =>
    Effect.gen(function* () {
      yield* cancelActive;
      const generation = yield* Ref.updateAndGet(activeGeneration, (current) => current + 1);
      const ready = yield* Deferred.make<void, E>();
      const completed = yield* Deferred.make<void, E>();
      yield* SubscriptionRef.set(state, {
        generation,
        phase: "synchronizing",
        snapshot: Option.none(),
      });

      const applyItem = Effect.fn("HostViewSynchronization.applyItem")(function* (
        item: OrchestrationShellStreamItem,
      ) {
        const shouldSignalReady = yield* SubscriptionRef.modify(
          state,
          (current): readonly [boolean, SynchronizedHostViewState] => {
            if (current.generation !== generation) {
              return [false, current];
            }
            if (item.kind === "ready") {
              if (Option.isNone(current.snapshot)) {
                return [false, current];
              }
              return [true, { ...current, phase: "ready" }];
            }
            if (item.kind === "snapshot") {
              if (Option.isSome(current.snapshot)) {
                return [false, current];
              }
              return [
                false,
                {
                  generation,
                  phase: "synchronizing",
                  snapshot: Option.some(item.snapshot),
                },
              ];
            }
            if (
              Option.isNone(current.snapshot) ||
              item.sequence <= current.snapshot.value.snapshotSequence
            ) {
              return [false, current];
            }
            return [
              false,
              {
                ...current,
                snapshot: Option.some(applyShellStreamEvent(current.snapshot.value, item)),
              },
            ];
          },
        );
        if (shouldSignalReady) {
          yield* Deferred.succeed(ready, undefined);
        }
      });

      const consumer = stream.pipe(
        Stream.runForEach(applyItem),
        Effect.onExit((exit) =>
          Deferred.done(completed, exit).pipe(
            Effect.andThen(
              exit._tag === "Failure" ? Deferred.done(ready, exit) : Deferred.interrupt(ready),
            ),
            Effect.asVoid,
          ),
        ),
      );
      const consumerFiber = yield* forkClientTransportSubscription(
        resources,
        {
          ownerIdentity,
          identity: "host-view",
          lifetime: "connection",
        },
        consumer,
      );
      yield* Ref.set(
        activeCleanup,
        Fiber.interrupt(consumerFiber).pipe(
          Effect.andThen(Deferred.interrupt(ready)),
          Effect.andThen(Deferred.interrupt(completed)),
          Effect.asVoid,
        ),
      );
      return { ready: Deferred.await(ready), completed: Deferred.await(completed) };
    });

  return { state, synchronize, invalidate } satisfies HostViewSynchronization;
});
